import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Phone,
  FolderOpen,
  BarChart3,
  Users,
  FileText,
  Grid3X3,
  Settings,
  HelpCircle,
  UserPlus,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import { useIsMobile } from "@/shared/hooks/use-mobile";

export function AppSidebar() {
  const { state, setOpenMobile } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const collapsed = state === "collapsed";
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const mainNavItems = [
    { title: t("nav.home"), url: "/dashboard" },
    { title: t("nav.calls"), url: "/calls" },
    { title: t("nav.customers"), url: "/contacts" },
    { title: t("nav.invoices"), url: "/invoices" },
  ];

  const footerItems = [
    { title: t("nav.support"), url: "/support", icon: HelpCircle },
    { title: t("nav.referFriend"), url: "/refer", icon: UserPlus },
  ];

  return (
    <Sidebar className={`${isMobile ? "" : collapsed ? "w-14" : "w-60"} bg-sidebar`} collapsible={isMobile ? "offcanvas" : "icon"}>
      <SidebarContent className="bg-sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-sidebar-border">
          <NavLink to="/dashboard" onClick={handleNavClick} className="block">
            <div className="bg-sidebar-primary text-sidebar-primary-foreground px-4 py-2 rounded-lg font-bold text-2xl text-center cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center gap-2">
              {collapsed ? (
                <span className="text-sm">w</span>
              ) : (
                <span>wlkthru</span>
              )}
            </div>
          </NavLink>
        </div>

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => {
                const isActive = currentPath === item.url || currentPath.startsWith(`${item.url}/`);

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        onClick={handleNavClick}
                        className={isActive ? "bg-sidebar-accent/50 text-sidebar-accent-foreground font-medium" : "hover:bg-sidebar-accent/50"}
                      >
                        {!collapsed && <span className="text-sm">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-sidebar border-t border-sidebar-border">
        <SidebarMenu>
          {footerItems.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild>
                {(() => {
                  const isActive = currentPath === item.url || currentPath.startsWith(`${item.url}/`);
                  return (
                    <NavLink 
                      to={item.url} 
                      onClick={handleNavClick}
                      className={isActive ? "bg-sidebar-accent/50 text-sidebar-accent-foreground font-medium" : "hover:bg-sidebar-accent/50"}
                    >
                      {item.icon && <item.icon className="h-5 w-5" />}
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  );
                })()}
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
